import React, { useEffect, useMemo, useState } from 'react';
import { Config } from 'config/envConfig';
import { Button, Dropdown, Input, LoadingView, Toggle } from '@quyva/shared-ui';
import {
    faCopy,
    faExternalLink,
    faSave,
    faFont,
    faPalette,
    faAdd,
    faClose,
    faPencil,
    faBriefcase,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { supportedFonts, toSentenceCase } from '@quyva/common';
import { supportedThemes, ThemeViewer } from '../themes';
import { getUrl } from './pdfDisplay/utils';
import axios from 'axios';
import { message, Tag } from 'antd';
export const RepositoryWebAccessSettings = ({
    user,
    selectedRepo,
    sharedRepos,
    isSmallScreen,
    onDirtyChange,
}) => {
    const mappingRepoId = selectedRepo?.repoid;
    const isVirtualRepo =
        selectedRepo?.parent_repo != null ||
        selectedRepo?.parent_repo_id != null;
    const parentRepoId =
        selectedRepo?.parent_repo ??
        selectedRepo?.parent_repo_id ??
        mappingRepoId;

    useEffect(() => {
        setDirty(false);
    }, [mappingRepoId]);

    const [isLoading, setIsLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('');
    const [isShared, setIsShared] = useState(false);

    const [companyLogoFile, setCompanyLogoFile] = useState(undefined);

    const [orgBranding, setOrgBranding] = useState(null);

    const [webAccessSettings, setWebAccessSettings] = useState(null);
    const [initialWebAccessSettings, setInitialWebAccessSettings] =
        useState(null);

    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isMappingEnabledDraft, setIsMappingEnabledDraft] = useState(false);
    const [initialIsMappingEnabledDraft, setInitialIsMappingEnabledDraft] =
        useState(false);

    const setDirty = (dirty) => {
        const next = !!dirty;
        setHasUnsavedChanges(next);
        onDirtyChange?.(next);
    };

    useEffect(() => {
        for (let index = 0; index < (sharedRepos || []).length; index++) {
            const repo = sharedRepos[index];
            if (repo.repoid === mappingRepoId) {
                setIsShared(true);
                break;
            }
        }
    }, [mappingRepoId, sharedRepos]);

    useEffect(() => {
        const init = async () => {
            if (!mappingRepoId) return;

            try {
                setIsLoading(true);
                setLoadingText('Loading web access settings ...');

                let loadedSettings = null;

                try {
                    const orgBrandingUrl = getUrl(
                        `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/customer_chat_routes/get-org-branding`
                    );
                    const encodedToken = encodeURIComponent(user?.token || '');
                    const orgResp = await axios.get(orgBrandingUrl, {
                        headers: {
                            Authorization: encodedToken,
                        },
                    });
                    setOrgBranding(orgResp?.data || null);
                } catch (e) {
                    setOrgBranding(null);
                }

                try {
                    const repoSettingsUrl = getUrl(
                        `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/customer_chat_routes/get-repo-web-access-settings`
                    );

                    const resp = await axios.get(repoSettingsUrl, {
                        params: {
                            repo_id: parentRepoId,
                            virtual_repo_id: isVirtualRepo
                                ? mappingRepoId
                                : null,
                        },
                    });

                    loadedSettings = resp?.data || null;
                    setWebAccessSettings(loadedSettings);
                    setInitialWebAccessSettings(loadedSettings);
                    setCompanyLogoFile(undefined);
                } catch (e) {
                    loadedSettings = null;
                    setWebAccessSettings(null);
                    setInitialWebAccessSettings(null);
                    setCompanyLogoFile(undefined);
                }

                const enabled = !!loadedSettings?.is_enabled;
                setIsMappingEnabledDraft(enabled);
                setInitialIsMappingEnabledDraft(enabled);
                setDirty(false);
            } finally {
                setIsLoading(false);
                setLoadingText('');
            }
        };

        init();
    }, [mappingRepoId]);

    const isWebAccessEnabledEffective = useMemo(() => {
        return !!isMappingEnabledDraft;
    }, [isMappingEnabledDraft]);

    const supportedThemeColors = useMemo(
        () => Object.keys(supportedThemes || {}),
        []
    );
    const supportedFontKeys = useMemo(
        () => Object.keys(supportedFonts || {}),
        []
    );

    const themeDropdown = useMemo(
        () =>
            supportedThemeColors.map((color) => {
                return {
                    key: color,
                    label: (
                        <ThemeViewer
                            supportedThemes={supportedThemes}
                            theme={color}
                        />
                    ),
                    icon: null,
                    iconColor: null,
                    selected: color === webAccessSettings?.theme,
                    action: () => {
                        setWebAccessSettings((prev) => {
                            const base =
                                prev && typeof prev === 'object' ? prev : {};
                            return { ...base, theme: color };
                        });
                        setDirty(true);
                    },
                };
            }),
        [supportedThemeColors, webAccessSettings?.theme]
    );

    const fontDropdown = useMemo(
        () =>
            supportedFontKeys.map((fontKey) => {
                return {
                    key: fontKey,
                    label: (
                        <div className={`font-${fontKey}`}>
                            {toSentenceCase(fontKey)}
                        </div>
                    ),
                    icon: null,
                    iconColor: null,
                    selected: fontKey === webAccessSettings?.font,
                    action: () => {
                        setWebAccessSettings((prev) => {
                            const base =
                                prev && typeof prev === 'object' ? prev : {};
                            return { ...base, font: fontKey };
                        });
                        setDirty(true);
                    },
                };
            }),
        [supportedFontKeys, webAccessSettings?.font]
    );

    const repoWebAccessLinkUrl = useMemo(() => {
        if (!Config.PRODUCT_SUPPORT_DOMAIN) return null;
        if (!mappingRepoId) return null;
        if (!isWebAccessEnabledEffective) return null;

        const accessCode = webAccessSettings?.access_code;
        if (!accessCode) return null;
        const queryParams = [];
        if (webAccessSettings?.allow_audio_feature)
            queryParams.push('audio=true');
        const queryString = queryParams.length
            ? `?${queryParams.join('&')}`
            : '';
        return `${Config.PRODUCT_SUPPORT_DOMAIN}/product-support/${accessCode}${queryString}`;
    }, [
        mappingRepoId,
        webAccessSettings?.access_code,
        webAccessSettings?.allow_audio_feature,
        isWebAccessEnabledEffective,
    ]);

    const copyToClipboard = async (text) => {
        try {
            if (!text) return;
            await navigator.clipboard.writeText(text);
            message.success('Copied');
        } catch (e) {
            message.error('Failed to copy');
        }
    };

    const setRepoWebAccessEnabled = (enabled) => {
        setIsMappingEnabledDraft(!!enabled);
        setDirty(true);
    };

    const buildWebAccessSettingsFormData = () => {
        const formData = new FormData();
        formData.append('repo_id', String(parentRepoId));
        if (isVirtualRepo) {
            formData.append('virtual_repo_id', String(mappingRepoId));
        }
        formData.append('is_enabled', String(!!isMappingEnabledDraft));
        formData.append(
            'allow_audio_feature',
            String(!!webAccessSettings?.allow_audio_feature)
        );
        formData.append('theme', webAccessSettings?.theme || 'default');
        formData.append('font', webAccessSettings?.font || 'segoe');
        formData.append('web_heading', webAccessSettings?.web_heading || '');
        formData.append('bot_name', webAccessSettings?.bot_name || '');
        // greeting_message: send null-string "null" for LLM-generated, "" for disabled, or custom text
        // We transmit the actual value; the backend treats null (param absent) as "no change"
        // and an explicit empty string as "opted out".
        const greetingVal = webAccessSettings?.greeting_message;
        if (greetingVal !== undefined) {
            // Always send so admin can clear a previously set greeting
            formData.append('greeting_message', greetingVal ?? '');
        }
        if (companyLogoFile instanceof File) {
            formData.append('company_logo', companyLogoFile);
        } else if (companyLogoFile === null) {
            formData.append(
                'company_logo',
                new File([''], 'empty-logo.txt', { type: 'text/plain' })
            );
        }
        return formData;
    };

    const handleSaveWebAccessSettings = async () => {
        try {
            if (!selectedRepo?.repoid) return;

            const title = (webAccessSettings?.web_heading || '')
                .toString()
                .trim();
            if (!title) {
                message.error('TITLE is required');
                return;
            }

            setIsLoading(true);
            setLoadingText('Saving web access settings ...');

            const updateUrl = getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/customer_chat_routes/update-repo-web-access-settings`
            );

            const formData = buildWebAccessSettingsFormData();

            const resp = await axios.post(updateUrl, formData);

            const saved = resp?.data || null;
            if (saved) {
                setWebAccessSettings(saved);
                setInitialWebAccessSettings(saved);
                setCompanyLogoFile(undefined);
            }
            setInitialIsMappingEnabledDraft(
                !!(saved?.is_enabled ?? isMappingEnabledDraft)
            );

            setDirty(false);
        } finally {
            setIsLoading(false);
            setLoadingText('');
        }
    };

    if (isLoading) {
        return (
            <div className='w-[70vw] h-[79vh] flex justify-center items-center'>
                <LoadingView loadingText={loadingText} inLine={false} />
            </div>
        );
    }

    const linkUrlEffective = isWebAccessEnabledEffective
        ? repoWebAccessLinkUrl
        : null;

    const hasSavedWebAccessSettings = !!webAccessSettings?.access_code;

    const effectiveCompanyLogoPreviewSrc = (() => {
        if (companyLogoFile instanceof File)
            return URL.createObjectURL(companyLogoFile);
        if (companyLogoFile === null) return null;
        if (webAccessSettings?.company_logo)
            return `data:image/png;base64,${webAccessSettings.company_logo}`;
        if (!hasSavedWebAccessSettings && orgBranding?.logo) {
            return `data:image/png;base64,${orgBranding.logo}`;
        }
        return null;
    })();

    return (
        <div className='pt-2 pb-4 px-4 w-[70vw] max-w-3xl'>
            <div className='p-4 w-full flex-col px-6 py-4 rounded-xl bg-backgrounds-slate'>
                <div className='flex items-center mb-6 mt-4'>
                    <div className='w-2/4 font-semibold text-texts-secondary300'>
                        <label htmlFor='companyLogo'>Company Logo</label>
                    </div>
                    <div className='w-full md:w-2/4'>
                        <div className='flex items-center'>
                            <div
                                className='relative rounded-xl border flex items-center justify-center bg-backgrounds-white p-2'
                                style={{ width: '80px', height: '80px' }}
                            >
                                {effectiveCompanyLogoPreviewSrc ? (
                                    <img
                                        src={effectiveCompanyLogoPreviewSrc}
                                        alt='Company Logo'
                                        className='rounded-xl'
                                    />
                                ) : (
                                    <FontAwesomeIcon
                                        icon={faBriefcase}
                                        size='2x'
                                        className='text-texts-secondary300 rounded-xl p-4 bg-backgrounds-white'
                                    />
                                )}

                                <input
                                    id='companyLogoFileInput'
                                    type='file'
                                    accept='image/*'
                                    style={{ display: 'none' }}
                                    onChange={(event) => {
                                        const file = event.target.files?.[0];
                                        if (file) {
                                            if (
                                                !file.type ||
                                                !file.type.startsWith('image/')
                                            ) {
                                                message.error(
                                                    'Only image files are allowed'
                                                );
                                                event.target.value = '';
                                                return;
                                            }
                                            setCompanyLogoFile(file);
                                            setDirty(true);
                                        }
                                    }}
                                />
                            </div>

                            <div className='flex flex-col justify-between items-center px-4'>
                                <Button
                                    className={'my-2 w-28'}
                                    hoverText={
                                        effectiveCompanyLogoPreviewSrc
                                            ? 'Change this logo'
                                            : 'Add new logo'
                                    }
                                    text={
                                        effectiveCompanyLogoPreviewSrc
                                            ? 'Edit'
                                            : 'Add'
                                    }
                                    icon={
                                        effectiveCompanyLogoPreviewSrc
                                            ? faPencil
                                            : faAdd
                                    }
                                    type={'success'}
                                    onClick={() => {
                                        const el = document.getElementById(
                                            'companyLogoFileInput'
                                        );
                                        el?.click?.();
                                    }}
                                />

                                {effectiveCompanyLogoPreviewSrc && (
                                    <Button
                                        className={'my-2 w-28'}
                                        hoverText={'Remove this logo'}
                                        text={'Remove'}
                                        icon={faClose}
                                        type={'danger'}
                                        onClick={() => {
                                            setCompanyLogoFile(null);
                                            setWebAccessSettings((prev) => {
                                                const base =
                                                    prev &&
                                                    typeof prev === 'object'
                                                        ? prev
                                                        : {};
                                                return {
                                                    ...base,
                                                    company_logo: null,
                                                };
                                            });
                                            setDirty(true);
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className='flex items-center mb-6 mt-4'>
                    <div className='w-2/4 font-semibold text-texts-secondary300'>
                        <label htmlFor='webText'>Title</label>
                    </div>
                    <div className='w-full md:w-2/4'>
                        <div className='relative'>
                            <Input
                                type={'text'}
                                value={webAccessSettings?.web_heading || ''}
                                placeholder={'Required'}
                                onChange={(e) => {
                                    setWebAccessSettings((prev) => {
                                        const base =
                                            prev && typeof prev === 'object'
                                                ? prev
                                                : {};
                                        return {
                                            ...base,
                                            web_heading: e.target.value,
                                        };
                                    });
                                    setDirty(true);
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div className='flex items-center mb-6 mt-4'>
                    <div className='w-2/4 font-semibold text-texts-secondary300'>
                        <label htmlFor='botName'>Bot Name</label>
                    </div>
                    <div className='w-full md:w-2/4'>
                        <div className='relative'>
                            <Input
                                type={'text'}
                                value={webAccessSettings?.bot_name || ''}
                                placeholder={'Optional (default: Assistant)'}
                                onChange={(e) => {
                                    setWebAccessSettings((prev) => {
                                        const base =
                                            prev && typeof prev === 'object'
                                                ? prev
                                                : {};
                                        return {
                                            ...base,
                                            bot_name: e.target.value,
                                        };
                                    });
                                    setDirty(true);
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div className='flex items-start mb-6 mt-4'>
                    <div className='w-2/4 font-semibold text-texts-secondary300 pt-1'>
                        <label htmlFor='greetingMessage'>
                            Greeting Message
                        </label>
                        <p className='text-xs font-normal text-texts-secondary300 mt-1'>
                            Sent when a user opens the chat. Leave blank to
                            auto-generate from bot persona. Set to a single
                            space to disable.
                        </p>
                    </div>
                    <div className='w-full md:w-2/4'>
                        <textarea
                            id='greetingMessage'
                            className='w-full rounded border border-backgrounds-primary200 bg-backgrounds-white text-texts-secondary300 px-3 py-2 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-buttons-primary'
                            rows={3}
                            maxLength={500}
                            value={webAccessSettings?.greeting_message ?? ''}
                            placeholder="e.g. Hi! I'm here to help you with your queries. Ask me anything."
                            onChange={(e) => {
                                setWebAccessSettings((prev) => {
                                    const base =
                                        prev && typeof prev === 'object'
                                            ? prev
                                            : {};
                                    return {
                                        ...base,
                                        greeting_message: e.target.value,
                                    };
                                });
                                setDirty(true);
                            }}
                        />
                        <div className='text-xs text-texts-secondary300 text-right mt-0.5'>
                            {(webAccessSettings?.greeting_message || '').length}
                            /500
                        </div>
                    </div>
                </div>

                <div className='flex items-center mb-6'>
                    <div className='w-2/4 font-semibold text-texts-secondary300'>
                        <label htmlFor='allowAudioFeature'>
                            Allow Audio Feature
                        </label>
                    </div>
                    <div className='w-full md:w-1/4 flex'>
                        <Toggle
                            checked={
                                webAccessSettings?.allow_audio_feature ?? false
                            }
                            onChange={(value) => {
                                setWebAccessSettings((prev) => {
                                    const base =
                                        prev && typeof prev === 'object'
                                            ? prev
                                            : {};
                                    return {
                                        ...base,
                                        allow_audio_feature: !!value,
                                    };
                                });
                                setDirty(true);
                            }}
                            toggleTextClassName={''}
                        />
                    </div>
                </div>

                <div className='flex items-center mb-6'>
                    <div className='w-2/4 font-semibold text-texts-secondary300'>
                        <label htmlFor='font'>Font</label>
                    </div>
                    <div className='w-full md:w-2/4'>
                        <div className='relative w-full'>
                            <Dropdown
                                dropDownList={fontDropdown}
                                showButtonText={true}
                                buttonText={
                                    webAccessSettings?.font
                                        ? toSentenceCase(webAccessSettings.font)
                                        : 'Select Font'
                                }
                                titleText={null}
                                buttonHoverText={'Select Font'}
                                buttonClassName={
                                    'text-texts-tertiary400 mr-1 h-8 w-64'
                                }
                                listTitleText={
                                    <>
                                        <FontAwesomeIcon
                                            icon={faFont}
                                            className='mr-2 text-texts-secondary300'
                                        />
                                        <span>Fonts</span>
                                    </>
                                }
                                disabled={false}
                                placement={'right'}
                            />
                        </div>
                    </div>
                </div>

                <div className='flex items-center mb-6'>
                    <div className='w-2/4 font-semibold text-texts-secondary300'>
                        <label htmlFor='theme'>Theme</label>
                    </div>
                    <div className='w-full md:w-2/4'>
                        <div className='relative w-full'>
                            <Dropdown
                                dropDownList={themeDropdown}
                                showButtonText={true}
                                buttonText={
                                    <ThemeViewer
                                        supportedThemes={supportedThemes}
                                        theme={webAccessSettings?.theme}
                                    />
                                }
                                titleText={null}
                                buttonHoverText={'Select Theme'}
                                buttonClassName={
                                    'text-texts-tertiary400 mr-1 h-8 w-64'
                                }
                                listTitleText={
                                    <>
                                        <FontAwesomeIcon
                                            icon={faPalette}
                                            className='mr-2 text-texts-secondary300'
                                        />
                                        <span>Themes</span>
                                    </>
                                }
                                disabled={false}
                                placement={'right'}
                            />
                        </div>
                    </div>
                </div>

                {isShared && (
                    <div className='flex items-center mb-4'>
                        <Tag className='text-texts-tertiary400 bg-backgrounds-secondary'>
                            Shared
                        </Tag>
                    </div>
                )}

                <div className='mt-6 pt-6 border-t border-gray-200'>
                    <div className='flex items-center mb-4'>
                        <div className='w-2/4 font-semibold text-texts-secondary300'>
                            <label>Web Access Link</label>
                        </div>
                        <div className='w-full md:w-2/3'>
                            <div className='relative flex items-center'>
                                <div className='flex items-center relative w-full'>
                                    <Input
                                        type={'text'}
                                        value={linkUrlEffective || ''}
                                        placeholder={
                                            'Link will appear here once enabled'
                                        }
                                        disabled={true}
                                    />
                                    <div className='absolute right-2'>
                                        <Button
                                            className={`ml-2`}
                                            hoverText={'Copy link'}
                                            text={null}
                                            icon={faCopy}
                                            type={'success'}
                                            disabled={!linkUrlEffective}
                                            onClick={() => {
                                                if (linkUrlEffective) {
                                                    copyToClipboard(
                                                        linkUrlEffective
                                                    );
                                                } else {
                                                    message.error(
                                                        'No link available to copy'
                                                    );
                                                }
                                            }}
                                        />
                                    </div>
                                </div>

                                <Button
                                    className={`ml-3`}
                                    hoverText={'Open link'}
                                    text={'Open'}
                                    icon={faExternalLink}
                                    type={'success'}
                                    disabled={!linkUrlEffective}
                                    onClick={() => {
                                        if (linkUrlEffective) {
                                            window.open(
                                                linkUrlEffective,
                                                '_blank'
                                            );
                                        } else {
                                            message.error(
                                                'Web access link not available'
                                            );
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {!webAccessSettings && (
                    <div className='mt-4 text-texts-secondary300'>
                        Web access settings are not loaded.
                    </div>
                )}

                <div className='sticky bottom-0 z-10 -mx-6 px-6 py-2 bg-backgrounds-slate border-t border-backgrounds-primary200 flex items-center justify-between gap-3'>
                    <Toggle
                        checked={isWebAccessEnabledEffective}
                        onChange={(checked) => setRepoWebAccessEnabled(checked)}
                        toggleTextClassName={
                            'w-fit mr-2.5 text-texts-secondary300 font-semibold whitespace-nowrap'
                        }
                        toggleText={'Enable Web Access'}
                        title={
                            isWebAccessEnabledEffective
                                ? 'Web Access Enabled'
                                : 'Web Access Disabled'
                        }
                    />

                    <div className='flex items-center gap-2'>
                        <Button
                            hoverText={'Discard changes'}
                            text={'Cancel'}
                            type={'danger'}
                            disabled={!hasUnsavedChanges}
                            onClick={() => {
                                setWebAccessSettings(
                                    initialWebAccessSettings || null
                                );
                                setIsMappingEnabledDraft(
                                    !!initialIsMappingEnabledDraft
                                );
                                setCompanyLogoFile(undefined);
                                setDirty(false);
                            }}
                        />

                        <Button
                            hoverText={'Save web access settings'}
                            text={'Save'}
                            icon={faSave}
                            type={'success'}
                            disabled={!selectedRepo?.repoid}
                            className={'ml-2'}
                            onClick={handleSaveWebAccessSettings}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
